import { currentLang, getAccessToken, signOutFromWeb } from '../actions/authActions';
import { isPikError } from '../../types/pikError';
import { generateSearchParams, IParams } from './createParams';
import { notFound, redirect } from 'next/navigation';
import { getCountryIdCookie, getLatAndLongFromCookies } from '../actions/getCookies';
import { signOut } from 'next-auth/react';

export type HttpRequestOptions = RequestInit & {
  ignore404?: boolean;
  arrayFormat?: 'brackets' | 'repeat';
};

export default class HttpCLient {
  private baseUrl = process.env.NEXT_PUBLIC_PIK_BACKEND_URL;

  // public async getCoordinates(): Promise<{ latitude?: number; longitude?: number }> {
  //   // Always fetch fresh coordinates from cookies
  //   const cookieCoords = await getLatAndLongFromCookies();
  //   return cookieCoords;
  // }

  public async getCoordinates(): Promise<{ latitude?: number; longitude?: number }> {
    const cookieCoords = await getLatAndLongFromCookies();
    const result: { latitude?: number; longitude?: number } = {};

    if (cookieCoords.latitude) {
      const lat = parseFloat(cookieCoords.latitude);
      if (!isNaN(lat)) {
        result.latitude = lat;
      }
    }

    if (cookieCoords.longitude) {
      const lon = parseFloat(cookieCoords.longitude);
      if (!isNaN(lon)) {
        result.longitude = lon;
      }
    }

    return result;
  }

  private async handleRequest<T>(endpoint: string, options: HttpRequestOptions = {}): Promise<T> {
    const url = `${this.baseUrl}/${endpoint}`;

    const [token, lang, countryId] = await Promise.all([
      getAccessToken(),
      currentLang(),
      getCountryIdCookie(),
    ]);

    const isFormData = options.body instanceof FormData;

    try {
      const requestOptions: RequestInit = {
        headers: {
          ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
          tenant: 'pik',
          ...options.headers,
          ...(token && { Authorization: `Bearer ${token}` }),
          ...(lang && { 'Accept-Language': lang }),
          ...(countryId && { countryId }),
        },
        ...(options.body && {
          body: isFormData ? options.body : JSON.stringify(options.body),
        }),
        method: options.method ?? 'GET',

        ...(options.cache
          ? { cache: options.cache }
          : options.next?.revalidate !== undefined
            ? {}
            : { cache: 'no-store' }),

        ...(options.next && { next: options.next }),
      };

      const response = await fetch(url, requestOptions);

      // console.log(response, "dddddddd")

      // ================= 404 =================
      if (response.status === 404) {
        if (options.ignore404) {
          return { status: 404 } as T;
        }
        notFound(); // يعمل صفحة 404 في Next.js
      }

      // ================= 401 =================
      if (response.status === 401) {
        await fetch(`/api/auth/logout`, { method: 'GET' }).then(() => signOut());
        redirect('/');
      }

      // ================= 204 No Content =================
      if (response.status === 204) {
        return {} as T;
      }

      // Read body safely
      const text = await response.text();

      // ================= Empty body =================
      if (!text) {
        return {} as T;
      }

      // Try parse JSON safely
      let data: any;
      try {
        data = JSON.parse(text);
      } catch {
        // Not JSON (HTML, plain text, etc)
        data = text;
      }

      // ================= Handle Errors =================
      if (!response.ok) {
        if (isPikError(data)) {
          throw new Error(data.detail);
        }

        const message =
          (typeof data === 'object' && data?.detail) || response.statusText || 'Unknown error';

        throw new Error(message);
      }

      return data as T;
    } catch (error) {
      throw error;
    }
  }

  //   get function

  protected async get<T>(
    endpoint: string,
    params?: IParams,
    options: HttpRequestOptions = {},
  ): Promise<T> {
    const { latitude, longitude } = await this.getCoordinates();
    let url = `${endpoint}`;
    const queryParams = {
      ...(latitude && { latitude: latitude.toString() }),
      ...(longitude && { longitude: longitude.toString() }),
      ...params,
    };

    if (queryParams) {
      const searchParams = generateSearchParams(queryParams, {
        arrayFormat: options.arrayFormat,
      });
      url += `?${searchParams.toString()}`;
    }

    // Remove revalidate to ensure fresh data when coordinates change
    return this.handleRequest<T>(url, options);
  }
  //   post request

  protected async post<T>(
    endpoint: string,
    data?: any,
    options?: HttpRequestOptions,
    params?: IParams,
  ): Promise<any> {
    let url = endpoint;
    if (params) {
      const searchParams = generateSearchParams(params, {
        arrayFormat: options?.arrayFormat,
      });
      const queryString = searchParams.toString();
      if (queryString) {
        url += (url.includes('?') ? '&' : '?') + queryString;
      }
    }

    const requestOptions: HttpRequestOptions = {
      ...options,
      method: 'POST',
      body: data,
    };
    return this.handleRequest<T>(url, requestOptions);
  }

  //delete request
  protected async delete<T>(endpoint: string, options?: HttpRequestOptions): Promise<T> {
    const requestOptions: HttpRequestOptions = {
      ...options,
      method: 'DELETE',
    };
    return this.handleRequest<T>(endpoint, requestOptions);
  }

  //   put request
  protected async put<T>(endpoint: string, data?: any, options?: HttpRequestOptions): Promise<any> {
    const requestOptions: HttpRequestOptions = {
      ...options,
      method: 'PUT',
      body: data,
    };
    return this.handleRequest<T>(endpoint, requestOptions);
  }
}
